import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiTokensApi, type CreateApiTokenBody } from './api';
import { SecretPanel } from './SecretPanel';
import { keys } from '@/lib/query/keys';
import { useListState } from '@/lib/hooks/useListState';
import { useGate } from '@/lib/rbac/Gate';
import { useApiError } from '@/lib/query/useApiError';
import { formatRelative } from '@/lib/format';
import { DataView, ForbiddenBanner } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Pagination } from '@/lib/ui/Pagination';
import { PageHeader, MonoValue } from '@/lib/ui/Typography';
import { Tag } from '@/lib/ui/Tag';
import { Button } from '@/lib/ui/Button';
import { Dialog } from '@/lib/ui/Dialog';
import { ConfirmDialog } from '@/lib/ui/ConfirmDialog';
import { TextInput } from '@/lib/ui/Field';
import { Select } from '@/lib/ui/Select';
import { useToast } from '@/lib/ui';
import type { ApiToken, ApiTokenStatus, MintedApiToken, Role } from '@/lib/api/types';
import type { Permission } from '@/lib/rbac/permissions';

const STATUS_VARIANT: Record<ApiTokenStatus, 'teal' | 'amber' | 'error'> = {
  active: 'teal',
  expired: 'amber',
  revoked: 'error',
};

/** Expiry choices, longest-lived last so the risky option is not the default. */
const EXPIRY_DAYS = [30, 90, 365] as const;

/**
 * API tokens — the credential console for machine clients (the LLM connector, CI).
 *
 * Two things drive the design. First, a token is a live credential with write
 * access to the site: the page has to make "what can this thing actually do" and
 * "how do I turn it off" the two easiest questions to answer, which is why the
 * table leads with the effective permissions and the collection allowlist rather
 * than with metadata. Second, the secret exists exactly once — so the moment
 * after creation is the only moment that matters, and it gets a dedicated,
 * unmissable panel instead of a toast.
 */
export function ApiTokensPage() {
  const { t } = useTranslation();
  const { allowed } = useGate('users:manage');
  const qc = useQueryClient();

  const list = useListState();
  const [creating, setCreating] = useState(false);
  const [minted, setMinted] = useState<MintedApiToken | null>(null);
  // Fetched here, not inside the secret panel: by the time the panel opens the
  // secret is already in memory and a failed lookup would strand it.
  const options = useQuery({
    queryKey: keys.apiTokens.options(),
    queryFn: () => apiTokensApi.options(),
    enabled: allowed,
  });
  const [revoking, setRevoking] = useState<ApiToken | null>(null);

  const query = useQuery({
    queryKey: keys.apiTokens.list(list.params),
    queryFn: () => apiTokensApi.list({ ...list.params, include_revoked: true }),
    placeholderData: (prev) => prev,
    enabled: allowed,
  });

  if (!allowed) {
    return (
      <div>
        <PageHeader eyebrow={t('apiTokens.eyebrow')} title={t('apiTokens.title')} />
        <ForbiddenBanner message={t('apiTokens.forbidden')} />
      </div>
    );
  }

  const columns: Column<ApiToken>[] = [
    {
      id: 'name',
      header: t('apiTokens.columns.name'),
      cell: (r) => (
        <div className="flex flex-col gap-[2px]">
          <span className="font-medium text-ink-deep">{r.name}</span>
          <MonoValue className="text-label text-ink-mute">{`pdk_${r.prefix}…`}</MonoValue>
        </div>
      ),
    },
    {
      id: 'account',
      header: t('apiTokens.columns.account'),
      cell: (r) => (
        <div className="flex flex-col gap-[3px]">
          <span className="text-ink-mid">{r.account_name}</span>
          <span>
            <Tag variant="neutral">{t(`users.roles.${r.account_role}`)}</Tag>
          </span>
        </div>
      ),
    },
    {
      id: 'grants',
      header: t('apiTokens.columns.grants'),
      // The whole point of the page: what this credential can actually do, at a
      // glance, without opening anything.
      cell: (r) => (
        <div className="flex max-w-[280px] flex-wrap gap-[4px]">
          {r.effective_permissions.map((p) => (
            <Tag key={p} variant={p === 'content:publish' || p === 'content:delete' ? 'amber' : 'neutral'}>
              {p}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      id: 'collections',
      header: t('apiTokens.columns.collections'),
      cell: (r) =>
        r.collections.length === 0 ? (
          <span className="text-small text-signal-amber-ink">{t('apiTokens.allCollections')}</span>
        ) : (
          <div className="flex max-w-[220px] flex-wrap gap-[4px]">
            {r.collections.map((c) => (
              <Tag key={c} variant="neutral">
                {c}
              </Tag>
            ))}
          </div>
        ),
    },
    {
      id: 'last_used_at',
      header: t('apiTokens.columns.lastUsed'),
      align: 'end',
      cell: (r) => (
        <span className="text-ink-mute">
          {r.last_used_at ? formatRelative(r.last_used_at) : t('apiTokens.neverUsed')}
        </span>
      ),
    },
    {
      id: 'expires_at',
      header: t('apiTokens.columns.expires'),
      align: 'end',
      cell: (r) =>
        r.expires_at ? (
          <span className="text-ink-mute">{formatRelative(r.expires_at)}</span>
        ) : (
          <span className="text-signal-amber-ink">{t('apiTokens.neverExpires')}</span>
        ),
    },
    {
      id: 'status',
      header: t('apiTokens.columns.status'),
      cell: (r) => <Tag variant={STATUS_VARIANT[r.status]}>{t(`apiTokens.status.${r.status}`)}</Tag>,
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      cell: (r) =>
        r.status === 'revoked' ? (
          <span className="text-small text-ink-mute">—</span>
        ) : (
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setRevoking(r);
            }}
          >
            {t('apiTokens.revoke.action')}
          </Button>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t('apiTokens.eyebrow')}
        title={t('apiTokens.title')}
        count={query.data?.meta.pagination.total}
        actions={<Button onClick={() => setCreating(true)}>{t('apiTokens.newToken')}</Button>}
      />

      <p className="mb-lg max-w-[70ch] text-small text-ink-mid">{t('apiTokens.intro')}</p>

      <DataView
        query={query}
        isEmpty={(d) => d.data.length === 0}
        empty={
          <EmptyTokens onCreate={() => setCreating(true)} />
        }
      >
        {(d) => (
          <>
            <DataTable columns={columns} rows={d.data} rowKey={(r) => r.id} />
            {d.meta?.pagination && (
              <Pagination
                meta={d.meta.pagination}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
              />
            )}
          </>
        )}
      </DataView>

      {creating && (
        <CreateTokenDialog
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            void qc.invalidateQueries({ queryKey: keys.apiTokens.all() });
            setCreating(false);
            setMinted(result);
          }}
        />
      )}

      {minted && (
        <SecretPanel
          minted={minted}
          apiBaseUrl={options.data?.api_base_url ?? ''}
          onClose={() => setMinted(null)}
        />
      )}

      <RevokeDialog
        token={revoking}
        onClose={() => setRevoking(null)}
        onRevoked={() => {
          void qc.invalidateQueries({ queryKey: keys.apiTokens.all() });
          setRevoking(null);
        }}
      />
    </div>
  );
}

/**
 * The empty state carries the explanation, because this is a page most admins
 * will open once and never think about again. Telling them what a connector
 * token IS here is cheaper than a doc nobody opens.
 */
function EmptyTokens({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-md rounded-md border-[0.5px] border-dashed border-hairline-strong bg-canvas-bone p-2xl">
      <p className="eyebrow text-ink-mute">{t('apiTokens.empty.eyebrow')}</p>
      <h3 className="text-title text-ink-deep">{t('apiTokens.empty.title')}</h3>
      <p className="max-w-[65ch] text-small text-ink-mid">{t('apiTokens.empty.description')}</p>
      <ul className="flex list-disc flex-col gap-2xs ps-lg text-small text-ink-mid">
        <li>{t('apiTokens.empty.point1')}</li>
        <li>{t('apiTokens.empty.point2')}</li>
        <li>{t('apiTokens.empty.point3')}</li>
      </ul>
      <Button onClick={onCreate}>{t('apiTokens.newToken')}</Button>
    </div>
  );
}

// ─── Create ────────────────────────────────────────────────────────────────

function CreateTokenDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (result: MintedApiToken) => void;
}) {
  const { t } = useTranslation();
  const onError = useApiError();

  const options = useQuery({
    queryKey: keys.apiTokens.options(),
    queryFn: () => apiTokensApi.options(),
  });

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string>('__new__');
  const [accountName, setAccountName] = useState('');
  const [role, setRole] = useState<Role>('author');
  const [scopes, setScopes] = useState<Permission[]>([]);
  const [collections, setCollections] = useState<string[]>([]);
  const [expiresInDays, setExpiresInDays] = useState<number>(90);

  const roles = options.data?.roles ?? [];
  const accounts = options.data?.service_accounts ?? [];
  const creatingAccount = accountId === '__new__';

  // The role's permissions are the ceiling: scopes may only narrow them. Showing
  // the role's own list — from the server's matrix, never a copy — is what makes
  // that relationship visible instead of something you have to know.
  const selectedRole: Role = creatingAccount
    ? role
    : (accounts.find((a) => a.id === accountId)?.role ?? role);
  const rolePermissions = useMemo(
    () => roles.find((r) => r.role === selectedRole)?.permissions ?? [],
    [roles, selectedRole],
  );

  const create = useMutation({
    mutationFn: (body: CreateApiTokenBody) => apiTokensApi.create(body),
    onSuccess: onCreated,
    onError: (e) =>
      onError(e, {
        onValidation: (fields: Record<string, string>) =>
          setNameError(fields.name ?? fields['service_account.name'] ?? null),
      }),
  });

  const canSubmit =
    !!name.trim() && (creatingAccount ? !!accountName.trim() : !!accountId) && !options.isLoading;

  function toggle<T extends string>(value: T, current: T[], set: (next: T[]) => void) {
    set(current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('apiTokens.create.title')}
      description={t('apiTokens.create.description')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            loading={create.isPending}
            disabled={!canSubmit}
            onClick={() => {
              setNameError(null);
              create.mutate({
                name: name.trim(),
                ...(creatingAccount
                  ? { service_account: { name: accountName.trim(), role } }
                  : { service_account_id: accountId }),
                scopes,
                collections,
                expires_in_days: expiresInDays,
              });
            }}
          >
            {t('apiTokens.create.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-lg">
        <TextInput
          label={t('apiTokens.fields.name')}
          required
          autoComplete="off"
          placeholder={t('apiTokens.fields.namePlaceholder')}
          hint={t('apiTokens.hints.name')}
          value={name}
          error={nameError}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(null);
          }}
        />

        <div className="flex flex-col gap-md border-t-[0.5px] border-hairline pt-lg">
          <Select
            label={t('apiTokens.fields.account')}
            value={accountId}
            onValueChange={setAccountId}
            options={[
              { value: '__new__', label: t('apiTokens.fields.newAccount') },
              ...accounts.map((a) => ({ value: a.id, label: `${a.name} · ${a.role}` })),
            ]}
          />
          {creatingAccount && (
            <div className="grid gap-md sm:grid-cols-2">
              <TextInput
                label={t('apiTokens.fields.accountName')}
                required
                placeholder={t('apiTokens.fields.accountNamePlaceholder')}
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
              />
              <Select
                label={t('apiTokens.fields.role')}
                value={role}
                onValueChange={(v) => {
                  setRole(v as Role);
                  setScopes([]); // a scope from the old role could exceed the new one
                }}
                options={roles
                  .filter((r) => r.assignable)
                  .map((r) => ({ value: r.role, label: t(`users.roles.${r.role}`) }))}
              />
              <p className="text-small text-ink-mute sm:col-span-2">{t('apiTokens.hints.role')}</p>
            </div>
          )}
        </div>

        <fieldset className="flex flex-col gap-sm border-t-[0.5px] border-hairline pt-lg">
          <legend className="eyebrow text-ink-mute">{t('apiTokens.fields.scopes')}</legend>
          <p className="text-small text-ink-mid">{t('apiTokens.hints.scopes')}</p>
          <div className="flex flex-wrap gap-xs">
            {rolePermissions.map((permission) => {
              const on = scopes.includes(permission);
              return (
                <TogglePill
                  key={permission}
                  label={permission}
                  pressed={on}
                  onToggle={() => toggle(permission, scopes, setScopes)}
                />
              );
            })}
          </div>
          {scopes.length === 0 && (
            <p className="text-small text-ink-mute">{t('apiTokens.hints.scopesEmpty')}</p>
          )}
        </fieldset>

        <fieldset className="flex flex-col gap-sm border-t-[0.5px] border-hairline pt-lg">
          <legend className="eyebrow text-ink-mute">{t('apiTokens.fields.collections')}</legend>
          <p className="text-small text-ink-mid">{t('apiTokens.hints.collections')}</p>
          <div className="flex flex-wrap gap-xs">
            {(options.data?.collections ?? []).map((c) => (
              <TogglePill
                key={c}
                label={c}
                pressed={collections.includes(c)}
                onToggle={() => toggle(c, collections, setCollections)}
              />
            ))}
          </div>
          {collections.length === 0 && (
            <p className="text-small text-signal-amber-ink">
              {t('apiTokens.hints.collectionsEmpty')}
            </p>
          )}
        </fieldset>

        <div className="flex flex-col gap-sm border-t-[0.5px] border-hairline pt-lg">
          <Select
            label={t('apiTokens.fields.expiry')}
            value={String(expiresInDays)}
            onValueChange={(v) => setExpiresInDays(Number(v))}
            options={[
              ...EXPIRY_DAYS.filter((d) => d <= (options.data?.max_ttl_days ?? 365)).map((d) => ({
                value: String(d),
                label: t('apiTokens.expiry.days', { count: d }),
              })),
              { value: '0', label: t('apiTokens.expiry.never') },
            ]}
          />
          {expiresInDays === 0 && (
            <p role="alert" className="text-small text-signal-amber-ink">
              {t('apiTokens.hints.neverExpiresWarning')}
            </p>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/**
 * A pill that reads as a choice rather than a chip. `aria-pressed` (not a
 * checkbox) because the visual is a toggle, and a screen reader should hear the
 * same thing a sighted user sees.
 */
function TogglePill({
  label,
  pressed,
  onToggle,
}: {
  label: string;
  pressed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onToggle}
      className={
        'rounded-xs border-[0.5px] px-[9px] py-[5px] font-mono text-label uppercase tracking-[0.1em] ' +
        'transition-colors duration-almanac ease-almanac focus-visible:shadow-focus focus:outline-none ' +
        (pressed
          ? 'border-accent-blue bg-accent-blue text-canvas-paper'
          : 'border-hairline-strong bg-canvas-sand text-ink-mute hover:border-accent-blue hover:text-ink-deep')
      }
    >
      {label}
    </button>
  );
}

// ─── Revoke ────────────────────────────────────────────────────────────────

function RevokeDialog({
  token,
  onClose,
  onRevoked,
}: {
  token: ApiToken | null;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const onError = useApiError();

  const revoke = useMutation({
    mutationFn: (id: string) => apiTokensApi.revoke(id),
    onSuccess: () => {
      toast.success(t('apiTokens.toasts.revoked', { name: token?.name ?? '' }));
      onRevoked();
    },
    onError: (e) => {
      onError(e);
      onClose();
    },
  });

  return (
    <ConfirmDialog
      open={!!token}
      onOpenChange={(o) => !o && onClose()}
      title={t('apiTokens.revoke.title', { name: token?.name ?? '' })}
      description={t('apiTokens.revoke.description')}
      confirmLabel={t('apiTokens.revoke.confirm')}
      tone="danger"
      loading={revoke.isPending}
      onConfirm={() => token && revoke.mutate(token.id)}
    />
  );
}
